import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trophy, Medal, Flame, Star, GraduationCap, Zap } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Footer } from "../components/Footer";
import api from "../lib/api";

export const BADGE_ICONS = {
  first_step: Flame,
  perfect: Star,
  consistent: Zap,
  graduate: GraduationCap,
  elite: Trophy,
};

export default function Leaderboard() {
  const { t } = useLang();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/leaderboard").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);

  const medal = (rank) => {
    if (rank === 1) return "bg-[#1D4ED8] text-white";
    if (rank === 2) return "bg-[#EEF4FF] text-[#0B1B3A]";
    if (rank === 3) return "bg-[#1D4ED8] text-white";
    return "bg-[#F1F5F9] text-[#0B1B3A]";
  };

  return (
    <div data-testid="leaderboard-page">
      <div className="max-w-5xl mx-auto px-5 sm:px-6 pt-14 pb-20">
        <span className="eyebrow">
          <span className="w-7 h-px bg-[#1D4ED8]" />
          {t("lb.subtitle")}
        </span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl mt-5 text-[#0B1B3A]" data-testid="leaderboard-title">
          {t("lb.title")}
        </h1>

        {rows === null ? (
          <p className="mt-10 text-[#64748B]">{t("common.loading")}</p>
        ) : rows.length === 0 ? (
          <div className="card-dark p-14 mt-10 text-center text-[#64748B]" data-testid="leaderboard-empty">{t("lb.empty")}</div>
        ) : (
          <div className="mt-10 card-dark overflow-hidden divide-y divide-[#E2E8F0]" data-testid="leaderboard-table">
            <div className="hidden sm:grid grid-cols-12 gap-4 px-5 py-4 text-[11px] font-bold uppercase tracking-[0.2em] text-[#94A3B8]">
              <span className="col-span-1">{t("lb.rank")}</span>
              <span className="col-span-5">{t("lb.student")}</span>
              <span className="col-span-2">{t("gam.level")}</span>
              <span className="col-span-2">{t("lb.lessons")}</span>
              <span className="col-span-2 text-right">{t("lb.xp")}</span>
            </div>
            {rows.map((r, i) => (
              <motion.div
                key={r.user_id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: Math.min(i, 10) * 0.04 }}
                className={`grid grid-cols-12 gap-4 items-center px-5 py-4 ${r.is_me ? "bg-[#1D4ED8]/10" : "hover:bg-[#F8FAFC]"} transition-colors duration-200`}
                data-testid={`leaderboard-row-${r.rank}`}
              >
                <span className="col-span-2 sm:col-span-1">
                  <span className={`w-9 h-9 rounded-full flex items-center justify-center font-heading font-black text-sm ${medal(r.rank)}`}>
                    {r.rank}
                  </span>
                </span>
                <span className="col-span-10 sm:col-span-5 flex items-center gap-2 min-w-0">
                  <span className="font-bold text-[#0B1B3A] truncate">{r.name}</span>
                  {r.is_me && <span className="badge-lime shrink-0">{t("lb.you")}</span>}
                  <span className="flex items-center gap-1 shrink-0">
                    {r.badges.slice(0, 4).map((b) => {
                      const Icon = BADGE_ICONS[b] || Medal;
                      return <Icon key={b} className="w-4 h-4 text-[#1D4ED8]" title={t(`badge.${b}`)} />;
                    })}
                  </span>
                </span>
                <span className="col-span-4 sm:col-span-2 text-xs font-bold uppercase tracking-wider text-[#475569]">{r.level}</span>
                <span className="col-span-4 sm:col-span-2 text-sm text-[#475569]">{r.completed_lessons}</span>
                <span className="col-span-4 sm:col-span-2 text-right font-heading font-black text-xl text-[#1D4ED8]">{r.xp}</span>
              </motion.div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
